import { ReactNode, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import TextWinds from '../../../components/common/TextWinds';
import HFbutton from '../../../components/button/HFbutton';
import { LoadingSpinner } from '../../../components/Loading/LoadingSpinner';
import BasicInfoSection from '../../../components/student/profile/BasicInfoSection';
import GuardianInfoSection from '../../../components/student/profile/GuardianInfoSection';
import ClassInfoSection from '../../../components/student/profile/ClassInfoSection';
import PasswordSection from '../../../components/student/profile/PasswordSection';
import { AccordionControlProps } from '../../../components/common/form/AccordionPanel';
import { getStudentProfile } from '../../../api/utils/student/profile/apis';
import { StudentProfile } from '../../../constants/types/student/profile/type';
import { StudentProfileContext } from '../StudentProfileContext';

/** 아코디언 카드 한 칸. 카드 단위로 펼치고 수정한다. */
type CardKey = 'basic' | 'guardian' | 'password' | 'class';

/**
 * 학생 > 내 정보 화면
 * @description
 * - 각 영역을 접었다 펴는 아코디언 카드로 보여줍니다.
 * - 각 영역 카드는 모두 자신의 폼과 저장을 스스로 처리합니다.
 * - 이 페이지 컴포넌트는 조회와 카드의 펼침/수정 상태만 관리합니다.
 * - 한 번에 한 카드만 수정할 수 있어야 하므로 수정 상태는 카드끼리 공유합니다.
 */
const StudentProfilePage = () => {
  const { setStudentProfile } = useContext(StudentProfileContext);

  /** 다른 화면에서 특정 카드를 펼친 채로 넘어올 때 쓴다. (ex. 내 학습 에서 클래스 추가하기 버튼 클릭하면 class 카드가 펼쳐진채로 진입) */
  const openSection = (useLocation().state as { openSection?: CardKey } | null)
    ?.openSection;

  /** 현재 수정 중인 카드. null이면 전체 조회 상태 */
  const [editCard, setEditCard] = useState<CardKey | null>(null);

  /** 펼쳐진 카드. 처음에는 모두 접어두고 여러 개를 함께 펼칠 수 있다. */
  const [openCards, setOpenCards] = useState<CardKey[]>(
    openSection ? [openSection] : [],
  );

  /** 조회한 학생 정보 */
  const [profile, setProfile] = useState<StudentProfile>();

  /** 조회 실패 여부 */
  const [loadFailed, setLoadFailed] = useState(false);

  /** 조회 초기화 */
  const init = () => {
    setLoadFailed(false);
    getStudentProfile()
      .then((data) => {
        setProfile(data);
        // 비밀번호/클래스 카드도 같은 값을 보도록 컨텍스트를 함께 갱신한다.
        setStudentProfile(data);
      })
      .catch((err) => {
        console.error(err);
        setLoadFailed(true);
      });
  };

  /** 처음 페이지 진입 시 조회 */
  useEffect(() => {
    init();
  }, []);

  /** 제목 줄 클릭. 수정 중인 카드는 입력이 사라지지 않도록 접지 않는다. */
  const toggleCard = (card: CardKey) => {
    if (editCard === card) return;

    setOpenCards((cards) =>
      cards.includes(card)
        ? cards.filter((item) => item !== card)
        : [...cards, card],
    );
  };

  /** 카드를 펼치면서 수정을 시작한다. (접힌 채로 수정되는 일이 없도록) */
  const startEdit = (card: CardKey) => {
    setOpenCards((cards) => (cards.includes(card) ? cards : [...cards, card]));
    setEditCard(card);
  };

  /** 카드 한 칸의 펼침/수정 상태. 저장은 카드가 스스로 처리한다. */
  const cardProps = (card: CardKey): AccordionControlProps => ({
    isOpen: openCards.includes(card),
    onToggle: () => toggleCard(card),
    isEditing: editCard === card,
    // 다른 카드를 수정하는 중이면 이 카드는 수정을 시작할 수 없다.
    disabled: editCard !== null && editCard !== card,
    onEdit: () => startEdit(card),
    onClose: () => setEditCard(null),
  });

  // 조회 실패 / 조회 중에는 카드 대신 상태만 보여준다.
  if (loadFailed || !profile) {
    return (
      <ProfileLayout>
        <div className="rounded-lg bg-white p-4 shadow-card">
          {loadFailed ? (
            <div className="flex min-h-[200px] flex-col items-center justify-center gap-4">
              <TextWinds type="content_body2" color="gray7">
                내 정보를 불러오지 못했습니다.
              </TextWinds>
              <div className="w-[160px]">
                <HFbutton height={36} onClick={init}>
                  다시 시도
                </HFbutton>
              </div>
            </div>
          ) : (
            <LoadingSpinner message="데이터 조회중" />
          )}
        </div>
      </ProfileLayout>
    );
  }

  return (
    <ProfileLayout>
      <div className="flex flex-col gap-4">
        {/* 기본 정보 */}
        <ProfileCard>
          <BasicInfoSection
            profile={profile}
            section={cardProps('basic')}
            onSaved={init}
          />
        </ProfileCard>

        {/* 보호자 정보 */}
        <ProfileCard>
          <GuardianInfoSection
            profile={profile}
            section={cardProps('guardian')}
            onSaved={init}
          />
        </ProfileCard>

        {/* 클래스 정보 */}
        <ProfileCard>
          <ClassInfoSection section={cardProps('class')} />
        </ProfileCard>

        {/* 비밀번호 변경 */}
        <ProfileCard>
          <PasswordSection section={cardProps('password')} />
        </ProfileCard>
      </div>
    </ProfileLayout>
  );
};

/** 아코디언 카드 한 칸의 껍데기 */
const ProfileCard = ({ children }: { children: ReactNode }) => (
  <div className="rounded-lg bg-white p-4 shadow-card xl:p-5">{children}</div>
);

/** 헤더 + 본문 공통 레이아웃. 조회 중/실패 화면도 같은 자리에 보여준다. */
const ProfileLayout = ({ children }: { children: ReactNode }) => (
  <div className="w-full bg-gray0 pb-10 pt-6">
    <div className="mx-auto flex w-full max-w-[900px] flex-col px-5">
      <header className="mb-5 min-w-0">
        <TextWinds type="title_h2" xlType="title_h3" className="mb-[2px]">
          내 정보
        </TextWinds>
        <TextWinds type="content_body3">
          각 항목별로 내 정보를 확인하고 수정할 수 있어요.
        </TextWinds>
      </header>
      {children}
    </div>
  </div>
);

export default StudentProfilePage;
